import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hurricaneApi } from '../api';
import { conditionLabel } from '../constants';
import { ROUTES } from '../../../constants/routes';

const OUTCOME_LABELS = {
  passed_no_photos: 'Passed (no photos)',
  passed_with_notes: 'Passed with notes',
  has_damage: 'Has damage',
};

function outcomeLabel(value) {
  return OUTCOME_LABELS[value] || value || '—';
}

function HurricaneReportView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await hurricaneApi.reportDetail(id);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

  const photosForItem = (itemId) =>
    (data?.damageItemPhotos || []).filter((p) => Number(p.item_id) === Number(itemId));

  if (loading) {
    return (
      <div className="app-shell-page">
        <div className="app-loading text-muted" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="app-shell-page">
        <div className="alert alert-danger py-2">{error || 'Report not found'}</div>
        <button
          type="button"
          className="btn btn-sm bg-white text-dark border rv-report-no-print"
          onClick={() => navigate(ROUTES.HURRICANE_REPORTS)}
        >
          Back to reports
        </button>
      </div>
    );
  }

  const staff = data.staff || {};

  return (
    <div className="app-shell-page rv-report-view">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 rv-report-no-print">
        <button
          type="button"
          className="btn btn-sm bg-white text-dark border"
          onClick={() => navigate(ROUTES.HURRICANE_REPORTS)}
        >
          Back to reports
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <div id="rv-report-print-root" className="card app-card bg-white">
        <div className="card-body">
          <h2 className="h5 mb-3">Hurricane damage report</h2>
          <p className="text-muted small mb-3">
            {data.hurricane_name} · {fmt(data.inspected_at)}
          </p>

          <div className="row small mb-4">
            <div className="col-md-6">
              <p className="mb-1"><strong>Community:</strong> {data.property_name}</p>
              <p className="mb-1"><strong>Site:</strong> {data.location_name || '—'}</p>
              <p className="mb-1"><strong>Site address:</strong> {data.location_address || '—'}</p>
              <p className="mb-1"><strong>Resident/guest:</strong> {data.resident_name || '—'}</p>
              <p className="mb-1"><strong>Outcome:</strong> {outcomeLabel(data.outcome)}</p>
            </div>
            <div className="col-md-6">
              <p className="mb-1"><strong>Community manager:</strong> {staff.community_manager_name || '—'}</p>
              <p className="mb-1"><strong>Manager email:</strong> {staff.community_manager_email || '—'}</p>
              <p className="mb-1"><strong>Assistant manager:</strong> {staff.assistant_manager_name || '—'}</p>
              <p className="mb-1"><strong>RVP:</strong> {staff.rvp_name || '—'}</p>
            </div>
          </div>

          {data.pass_comments && (
            <div className="mb-4">
              <h3 className="h6">Pass comments</h3>
              <p className="small mb-0" style={{ whiteSpace: 'pre-wrap' }}>{data.pass_comments}</p>
            </div>
          )}

          {data.reportPhotos?.length > 0 && (
            <div className="mb-4">
              <h3 className="h6 mb-2">Report photos</h3>
              <div className="row g-3 rv-report-photos">
                {data.reportPhotos.map((ph) => (
                  <div key={ph.id} className="col-6 col-md-4">
                    <img
                      src={ph.image_url}
                      alt="Report"
                      className="img-fluid rounded border w-100"
                      style={{ maxHeight: '280px', objectFit: 'contain' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <h3 className="h6 mb-2">Damage entries</h3>
          {!data.damageItems?.length ? (
            <p className="text-muted small mb-0">No damage lines recorded.</p>
          ) : (
            data.damageItems.map((it) => (
              <div key={it.id} className="border rounded p-3 mb-3 bg-white">
                <p className="mb-1">
                  <strong>{it.location_of_damage}</strong>
                  {' — '}
                  {conditionLabel(it.condition)}
                </p>
                {it.notes && (
                  <p className="small text-muted mb-2" style={{ whiteSpace: 'pre-wrap' }}>{it.notes}</p>
                )}
                {photosForItem(it.id).length > 0 ? (
                  <div className="row g-2 rv-report-photos">
                    {photosForItem(it.id).map((ph) => (
                      <div key={ph.id} className="col-6 col-md-4">
                        <img
                          src={ph.image_url}
                          alt={it.location_of_damage}
                          className="img-fluid rounded border w-100"
                          style={{ maxHeight: '280px', objectFit: 'contain' }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted small mb-0">No photos for this entry.</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default HurricaneReportView;
